/**
 * Pure DOM viewport visibility tracking for room-owned device elements.
 *
 * Per rule: think-in-systems — one shared observer, room-scoped state.
 */

// Branded device ids end-to-end so livekit.ts can feed the governor without casts.
import type { Device } from '@studio/models/device';

type DeviceId = Device['id'];

type ElementMetadata = {
  roomId: string;
  deviceId: DeviceId;
  isIntersecting: boolean;
};

type DeviceElements = Map<DeviceId, Set<Element>>;

// Duplicate viewers are independent registrations for the same room device.
const roomDevices = new Map<string, DeviceElements>();
const elementMetadata = new Map<Element, ElementMetadata>();

let observer: IntersectionObserver | null = null;

function getOrCreateObserver(): IntersectionObserver {
  if (!observer) {
    if (typeof IntersectionObserver === 'undefined') {
      throw new Error('IntersectionObserver not available in this environment');
    }

    observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        for (const entry of entries) {
          const metadata = elementMetadata.get(entry.target);
          if (metadata) {
            metadata.isIntersecting = entry.isIntersecting;
          }
        }
      },
      { threshold: 0.1, root: null }
    );
  }

  return observer;
}

/** Register one exact element for a room-owned device. */
export function observeDevice(roomId: string, deviceId: DeviceId, element: HTMLElement): void {
  const existing = elementMetadata.get(element);
  if (existing) {
    if (existing.roomId === roomId && existing.deviceId === deviceId) {
      return;
    }

    unobserveDevice(existing.roomId, existing.deviceId, element);
  }

  let devices = roomDevices.get(roomId);
  if (!devices) {
    devices = new Map();
    roomDevices.set(roomId, devices);
  }

  let elements = devices.get(deviceId);
  if (!elements) {
    elements = new Set();
    devices.set(deviceId, elements);
  }

  elements.add(element);
  elementMetadata.set(element, { roomId, deviceId, isIntersecting: false });
  getOrCreateObserver().observe(element);
}

/** Unregister only the element owned by the supplied room and device. */
export function unobserveDevice(roomId: string, deviceId: DeviceId, element: HTMLElement): void {
  const metadata = elementMetadata.get(element);
  if (metadata?.roomId !== roomId || metadata.deviceId !== deviceId) {
    return;
  }

  observer?.unobserve(element);
  elementMetadata.delete(element);

  const devices = roomDevices.get(roomId);
  const elements = devices?.get(deviceId);
  elements?.delete(element);

  if (elements?.size === 0) {
    devices?.delete(deviceId);
  }
  if (devices?.size === 0) {
    roomDevices.delete(roomId);
  }
}

/** Return devices with at least one intersecting viewer in the requested room. */
export function getVisibleDevices(roomId: string): ReadonlySet<DeviceId>;
/** Return the union of visible devices across rooms. */
export function getVisibleDevices(): ReadonlySet<DeviceId>;
export function getVisibleDevices(roomId?: string): ReadonlySet<DeviceId> {
  const visibleDevices = new Set<DeviceId>();
  const rooms = roomId === undefined ? roomDevices.values() : [roomDevices.get(roomId)].values();

  for (const devices of rooms) {
    if (!devices) continue;

    for (const [deviceId, elements] of devices) {
      for (const element of elements) {
        if (elementMetadata.get(element)?.isIntersecting) {
          visibleDevices.add(deviceId);
          break;
        }
      }
    }
  }

  return visibleDevices;
}

/** Remove every observed element owned by one room. */
export function clearRoom(roomId: string): void {
  const devices = roomDevices.get(roomId);
  if (!devices) return;

  for (const elements of devices.values()) {
    for (const element of elements) {
      observer?.unobserve(element);
      elementMetadata.delete(element);
    }
  }

  roomDevices.delete(roomId);
}

/** Reset shared state between unit tests. */
export function _resetForTest(): void {
  observer?.disconnect();
  observer = null;
  roomDevices.clear();
  elementMetadata.clear();
}
